import React, { forwardRef, memo, useCallback } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { GridSlotProps } from '@mui/x-data-grid-pro';

/**
 * NOTE: this checkbox is used for ALL checkboxes
 * in the datagrid when using baseCheckbox gridslots so
 * it will be applied to row, header, column selector, etc!
 *
 * DataGridPro does not provide a way to override
 * just the row checkbox easily, as far as I can tell.
 *
 * We need this component to stop the propagation of the click event
 * while keeping the row wrapped with a link to the search item.
 * Tried the wrapper on the SearchGridCell but the issue is that
 * the cell is virtualized and the height is not set to 100%,
 * and empty cells / cells with no data will not work either
 * especially in the 'summary' style view, youd have to click
 * on the text of a cell itself to trigger the menu/link.
 *
 * So on this component and/or additionally the SearchGridRow
 * we need to stop the propagation of the click event so row
 * does not navigate when clicking on the checkbox.
 *
 */

export type MuiDgBaseChekboxProps = GridSlotProps['baseCheckbox'];

// namespaced prop to avoid collision with mui props
// can props here as needed replacing [key:string]:any
// or prefix props with some unique thing eg 'customOnClick'
// or if need to can direct override mui props...
export interface SearchGridBaseCheckboxProps extends MuiDgBaseChekboxProps {
  customProps?: {
    [key: string]: any;
  };
}

export const SearchGridBaseCheckbox = memo(
  forwardRef<HTMLButtonElement, SearchGridBaseCheckboxProps>(
    ({ customProps, ...muiCheckboxProps }, ref) => {
      // Destructure `ref` out so it isn't spread onto <Checkbox> — we forward
      // it manually below. Leaving it in the spread causes a React 19 type
      // incompatibility between `RefObject<T | null>` and `RefObject<T>`.
      const {
        label,
        fullWidth,
        ref: _ref,
        slotProps,
        ...checkboxProps
      } = muiCheckboxProps as SearchGridBaseCheckboxProps & {
        label?: React.ReactNode;
        fullWidth?: boolean;
        ref?: React.Ref<HTMLButtonElement>;
        slotProps?: unknown;
      };

      const handleCheckboxClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
          // Prevent the row SearchGridRow event from
          // propagating up and triggering a navigation
          event.stopPropagation();
          // Call the mui onClick handler if it exists
          if (checkboxProps?.onClick) {
            checkboxProps?.onClick?.(event);
          }
        },
        [checkboxProps],
      );

      const checkbox = (
        <Checkbox
          ref={ref}
          {...checkboxProps}
          onClick={handleCheckboxClick}
        />
      );

      // MUI v8 passes a `label` prop when rendering rows in the columns management
      // panel. Mirror the behaviour of MUI's own BaseCheckbox: if a label is
      // provided, wrap the checkbox in a FormControlLabel so the text is visible.
      if (label) {
        return (
          <FormControlLabel
            control={checkbox}
            label={label}
            style={fullWidth ? { width: '100%' } : undefined}
          />
        );
      }

      return checkbox;
    },
  ),
);
SearchGridBaseCheckbox.displayName = 'SearchGridBaseCheckbox';
